import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../modal-new";
import PropTypes from "prop-types";
import useTrackGoals from "./helpers/useTrackGoal";
import SvgIcon from "../../SvgIcon";
import { DayPicker } from "react-day-picker";
import { parseTime } from "../../../views/athlete-portal/calendar/sleep-time/SleepTime";
import AreaGraph from "../../../views/athlete-portal/calendar/body-composition/components/AreaGraph";
import BarGraph from "../../../views/athlete-portal/calendar/body-composition/components/BarGraph";
import { RPEList } from "../../../views/athlete-portal/calendar/session-rpe/RPEList";
import { round } from "lodash";
import convert from "convert-units";
import submit from "submit-form";
import moment from "moment";

const OZ_PER_L = 33.8140227;

const ModalTitle = {
  steps: "Daily Steps",
  sleep: "Daily Sleep",
  bodyWeight: "Bodyweight",
  waterIntake: "Daily Water Intake",
};

const detailsTarget = {
  steps: "targetSteps",
  sleep: "targetSleep",
  bodyWeight: "targetWeight",
  waterIntake: "targetWaterIntake",
};

const CustomCaption = ({ currentMonth, onPrevClick, onNextClick }) => {
  return (
    <div className="calendar-caption">
      <button type="button" onClick={onPrevClick} className="rd-back"></button>
      <span className="calendar-title">
        {moment(currentMonth).format("MMMM, YYYY")}
      </span>
      <button type="button" className="rd-next" onClick={onNextClick}></button>
    </div>
  );
};

const DailyGoalsModal = ({ locals }) => {
  const { t } = useTranslation("coaching-portal");
  const { query, activeUser, dailyStats, clientGoals } = locals;
  const { units } = activeUser;
  const { goalId } = query;
  const unit = activeUser.units === "metric" ? "Kg" : "Lbs";
  const modalTitle = {
    steps: t("dailyGoalsModal.dailySteps"),
    sleep: t("dailyGoalsModal.dailySleep"),
    bodyWeight: t("dailyGoalsModal.bodyweight"),
  };

  const currentGoal = clientGoals.find((goal) => goal._id === goalId);
  const goalType = currentGoal?.type;
  const isMetric = units === "metric";
  const formatWater = (liters) =>
    isMetric ? `${round(liters, 1)} L` : `${round(liters * OZ_PER_L, 0)} oz`;

  const [sortedTableData, setSortedTableData] = useState();

  const [sortedRecent, setSortedRecent] = useState();
  const goalTarget = currentGoal?.details[detailsTarget[goalType]];
  const startDate = currentGoal?.utcStartDate;

  const [month, setMonth] = useState(moment().toDate());

  const handlePrevClick = () => {
    const newMonth = moment(month).subtract(1, "month");
    const startDateMoment = moment(startDate, "YYYYMMDD");

    // Only update the month if the new month is after the starting date
    if (newMonth.isSameOrAfter(startDateMoment, "month")) {
      setMonth(newMonth.toDate());
    }
  };
  const handleNextClick = () => {
    setMonth(moment(month).add(1, "month").toDate());
  };

  const {
    lastDaysAchieved,
    streaks,
    averageStat,
    chartData,
    tableData,
    calendarDates,
  } = useTrackGoals({
    goalType,
    units,
    dailyStats,
    goalTarget,
    startDate,
  });

  const { currentStreak, bestStreak } = streaks;

  const sortList = (newState) => {
    setSortedTableData(newState.dateFormattedData);
    setSortedRecent(newState.sortedRecent);
  };
  const tableDataToDisplay =
    goalType === "waterIntake" && units === "metric"
      ? tableData
      : {
          ...tableData,
          listData: tableData.listData.map((stat) => ({
            ...stat,
            waterIntake: round(Number(stat.waterIntake) * OZ_PER_L, 0),
          })),
        };
  return (
    <Modal
      className={`daily-goal-modal ${
        goalType === "bodyWeight" ? "bodyweight" : ""
      }`}
      title={modalTitle[goalType]}
      toClear={["goalId"]}
    >
      <div className="body">
        {goalType !== "bodyWeight" && (
          <>
            <div className="goal-information">
              <div className="current-goal-header">
                <p>{t("dailyGoalsModal.currentGoal")}</p>
                <div className="current-goal-info">
                  <p>
                    {goalType === "sleep"
                      ? parseTime(goalTarget)
                      : goalType === "waterIntake"
                        ? formatWater(goalTarget)
                        : `${goalTarget} ${t("dailyGoalsModal.steps")}`}
                  </p>
                  <button
                    data-button-type="primary"
                    onClick={() => {
                      submit("/actions/refresh?", {
                        method: "GET",
                        body: {
                          "modal[type]": "add-client-goal",
                          modalState: currentGoal.type,
                          goalId: currentGoal._id,
                        },
                      });
                    }}
                  >
                    <SvgIcon src={"/img/new-icons/edit-pencil.svg"} />
                  </button>
                </div>
              </div>
              <div className="goal-calendar">
                <DayPicker
                  month={month}
                  onMonthChange={(newMonth) =>
                    setMonth(moment(newMonth).toDate())
                  }
                  components={{
                    Caption: () => (
                      <CustomCaption
                        currentMonth={month}
                        onPrevClick={handlePrevClick}
                        onNextClick={handleNextClick}
                      />
                    ),
                  }}
                  modifiers={calendarDates.modifiers}
                  modifiersClassNames={calendarDates.modifiersClassNames}
                  showOutsideDays
                />
              </div>
              <div className="goal-streaks">
                <div className="goal-streak-row">
                  <div className="goal-streak-card">
                    <p>{t("dailyGoalsModal.currentStreak")}</p>
                    <div className="goal-streak-info">
                      <p>{currentStreak}</p>
                      <p>{t("dailyGoalsModal.days")}</p>
                    </div>
                  </div>
                  <div className="goal-streak-card">
                    <p>{t("dailyGoalsModal.dailyAverage")}</p>
                    <div className="goal-streak-info">
                      <p>
                        {goalType === "sleep"
                          ? parseTime(averageStat)
                          : goalType === "waterIntake"
                            ? formatWater(averageStat)
                            : averageStat}
                      </p>
                      <p>
                        /
                        {goalType === "sleep"
                          ? parseTime(goalTarget)
                          : goalType === "waterIntake"
                            ? formatWater(goalTarget)
                            : goalTarget}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="goal-streak-row">
                  <div className="goal-streak-card">
                    <p>{t("dailyGoalsModal.bestStreak")}</p>
                    <div className="goal-streak-info">
                      <p>{bestStreak}</p>
                      <p>{t("dailyGoalsModal.days")}</p>
                    </div>
                  </div>
                  <div className="goal-streak-card">
                    <p>{t("dailyGoalsModal.last7Days")}</p>
                    <div className="goal-streak-info">
                      <p>{lastDaysAchieved}</p>
                      <p>{t("dailyGoalsModal.achieved7")}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="separator"></div>
          </>
        )}

        <div className="goal-data">
          {goalType === "bodyWeight" && (
            <div className="current-goal-header">
              <p>{t("dailyGoalsModal.currentGoal")}</p>
              <div className="current-goal-info">
                <p>
                  {`${
                    activeUser.units === "metric"
                      ? round(goalTarget || 0, 2)
                      : round(
                          convert(goalTarget || 0)
                            .from("kg")
                            .to("lb"),
                          2
                        )
                  } ${unit}`}
                </p>
                <button
                  data-button-type="primary"
                  onClick={() => {
                    submit("/actions/refresh?", {
                      method: "GET",
                      body: {
                        "modal[type]": "add-client-goal",
                        modalState: currentGoal.type,
                        goalId: currentGoal._id,
                      },
                    });
                  }}
                >
                  <SvgIcon src={"/img/new-icons/edit-pencil.svg"} />
                </button>
              </div>
            </div>
          )}

          <div className="goal-chart">
            {goalType !== "bodyWeight" ? (
              <BarGraph
                data={chartData}
                targetValue={goalType}
                graphId={`${goalType}-graph-daily-modal`}
                average={averageStat}
                parseFn={
                  goalType === "sleep"
                    ? parseTime
                    : goalType === "waterIntake"
                      ? (val) => formatWater(val)
                      : undefined
                }
              />
            ) : (
              <AreaGraph
                data={chartData}
                targetValue={goalType}
                graphId={"bodyweight-graph-profile"}
                label={t("dailyGoalsModal.bodyWeightLabel")}
                description={t("dailyGoalsModal.weight")}
                units={unit}
              />
            )}
          </div>
          {tableData?.listData?.length > 0 && (
            <div className="goal-table">
              <RPEList
                dateFrom={tableData.dateFrom}
                dateTo={tableData.dateTo}
                list={sortedTableData || tableDataToDisplay.listData}
                columnsData={tableData.columnsData}
                sortedRecent={sortedRecent}
                updateState={sortList}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

DailyGoalsModal.propTypes = {
  locals: PropTypes.any,
};

export default DailyGoalsModal;
